import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { PageContent } from '../components/layout/PageContent';
import { championshipApi, matchApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users, Loader2, Trophy, Award, Play, CheckCircle, Medal, Shield, ChevronsUpDown, Check, Search, Tv
} from 'lucide-react';
import { toast } from 'sonner';
import MedalDeliveryWidget from '../components/MedalDeliveryWidget';
import DirectPlacementSection from '../components/DirectPlacementSection';
import SearchableCombobox from '../components/SearchableCombobox';
import { EstadoVazio } from '../components/common/Estados';

const PLACE_LABELS = { 1: 'Ouro', 2: 'Prata', 3: 'Bronze', 4: '4o Lugar' };
const PLACE_COLORS = { 1: 'medalha-ouro', 2: 'medalha-prata', 3: 'medalha-bronze', 4: 'text-[#71717A]' };

export default function CentralDeskPage() {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const contextId = params.id || searchParams.get('championship_id') || '';
  const [championships, setChampionships] = useState([]);
  const [brackets, setBrackets] = useState([]);
  const [selectedChampionship, setSelectedChampionship] = useState(contextId);
  const [selectedBracket, setSelectedBracket] = useState('');
  const [bracketComboOpen, setBracketComboOpen] = useState(false);
  const [bracketCodeQuery, setBracketCodeQuery] = useState('');
  const [bracketNumberQuery, setBracketNumberQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [resultData, setResultData] = useState({ winner_id: '', result_type: 'points', score1: 0, score2: 0 });
  const [saving, setSaving] = useState(false);
  // Single-athlete bracket result dialog state
  const [singleMatch, setSingleMatch] = useState(null);
  const [singleOutcome, setSingleOutcome] = useState('champion');
  const [liveRanking, setLiveRanking] = useState([]);
  const [teamRanking, setTeamRanking] = useState([]);
  const [rankingModality, setRankingModality] = useState('all'); // all | gi | nogi
  const [medalRefreshKey, setMedalRefreshKey] = useState(0);
  // Filtros da aba "Ranking Atletas" (recortes LOCAIS da lista ja carregada).
  // A Modalidade NAO tem filtro proprio aqui: continua no controle global do
  // TabsList (dono unico daquele estado).
  const [filterAthlete, setFilterAthlete] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterKids, setFilterKids] = useState('');
  const wsRef = useRef(null);

  // Modalidade do campeonato selecionado (gi | nogi | misto) e regra de ranking
  const selectedChampObj = championships.find(c => c._id === selectedChampionship);
  const champMisto = selectedChampObj?.modality === 'misto';
  const combineRanking = selectedChampObj?.combine_modality_ranking !== false;
  // combine=true -> Todas (padrao) + Kimono + No-Gi; combine=false -> so uma por vez
  const rankingOptions = combineRanking
    ? [{ value: 'all', label: 'Todas as modalidades' }, { value: 'gi', label: 'Kimono' }, { value: 'nogi', label: 'No-Gi' }]
    : [{ value: 'gi', label: 'Kimono' }, { value: 'nogi', label: 'No-Gi' }];

  // Ranking separado por modalidade (combine=false): comeca em "Kimono"
  useEffect(() => {
    if (champMisto && !combineRanking && rankingModality === 'all') {
      setRankingModality('gi');
    }
  }, [champMisto, combineRanking, rankingModality]);

  // ---------------------------------------------------------------------
  // Filtros da aba "Ranking Atletas"
  // ---------------------------------------------------------------------
  // Atleta/Equipe/Categoria sao apenas recortes da lista que JA esta na tela
  // (nenhuma consulta nova, nenhum dono novo de estado). O filtro Kids usa o
  // campo `is_kids` calculado pelo BACKEND — a regra do agrupamento tem um
  // dono unico e nao e reescrita aqui.
  const NO_TEAM = '__sem_equipe__';
  const categoryKeyOf = (r) => r.category_name || '(categoria não informada)';

  // Config do agrupamento Kids do campeonato selecionado (dono: campeonato)
  const champConfigKnown = !!selectedChampObj;
  const kidsEnabled = !!selectedChampObj?.kids_enabled;
  const kidsMaxAge = selectedChampObj?.kids_max_age || null;

  // Trocar de campeonato limpa os filtros: um recorte do campeonato anterior
  // esconderia o ranking novo.
  useEffect(() => {
    setFilterAthlete('');
    setFilterTeam('');
    setFilterCategory('');
    setFilterKids('');
  }, [selectedChampionship]);

  const athleteFilterOptions = useMemo(() => {
    const byId = new Map();
    liveRanking.forEach((r) => {
      const id = String(r.athlete_id || '');
      if (!id || byId.has(id)) return;
      byId.set(id, {
        value: id,
        label: r.athlete_name || '(atleta sem nome)',
        sublabel: r.team_name || 'Sem equipe',
      });
    });
    return [
      { value: '', label: 'Todos os atletas', sublabel: `${byId.size} com resultado no ranking` },
      ...[...byId.values()].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    ];
  }, [liveRanking]);

  const teamFilterOptions = useMemo(() => {
    const byId = new Map();
    liveRanking.forEach((r) => {
      const id = String(r.team_id || '') || NO_TEAM;
      const entry = byId.get(id);
      if (entry) { entry.count += 1; return; }
      byId.set(id, { value: id, label: r.team_name || 'Sem equipe', count: 1 });
    });
    const options = [...byId.values()]
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
      .map((t) => ({ value: t.value, label: t.label, sublabel: `${t.count} resultado${t.count > 1 ? 's' : ''}` }));
    return [{ value: '', label: 'Todas as equipes' }, ...options];
  }, [liveRanking]);

  const categoryFilterOptions = useMemo(() => {
    const byName = new Map();
    liveRanking.forEach((r) => {
      const name = categoryKeyOf(r);
      const entry = byName.get(name);
      if (entry) { entry.count += 1; return; }
      byName.set(name, { value: name, label: name, count: 1 });
    });
    const options = [...byName.values()]
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
      .map((c) => ({ value: c.value, label: c.label, sublabel: `${c.count} colocaç${c.count > 1 ? 'ões' : 'ão'}` }));
    return [{ value: '', label: 'Todas as categorias' }, ...options];
  }, [liveRanking]);

  const kidsFilterOptions = [
    { value: '', label: 'Todos' },
    { value: 'kids', label: '🧒 Kids', sublabel: kidsMaxAge ? `agrupamento Kids até ${kidsMaxAge} anos` : 'agrupamento Kids do campeonato' },
    { value: 'adults', label: '🧑 Demais categorias', sublabel: 'Juvenil, Adulto e Master' },
  ];

  const filteredLiveRanking = useMemo(() => liveRanking.filter((r) => {
    if (filterAthlete && String(r.athlete_id || '') !== filterAthlete) return false;
    if (filterTeam && (String(r.team_id || '') || NO_TEAM) !== filterTeam) return false;
    if (filterCategory && categoryKeyOf(r) !== filterCategory) return false;
    if (filterKids === 'kids' && r.is_kids !== true) return false;
    if (filterKids === 'adults' && r.is_kids === true) return false;
    return true;
  }), [liveRanking, filterAthlete, filterTeam, filterCategory, filterKids]);

  const hasActiveRankingFilters = !!(filterAthlete || filterTeam || filterCategory || filterKids);
  const clearRankingFilters = () => {
    setFilterAthlete('');
    setFilterTeam('');
    setFilterCategory('');
    setFilterKids('');
  };

  // Per-championship bracket number (1..N). The API now returns the official
  // number (official_number) computed in the same FJJPRIO print order as the
  // Staff PDF — single source of truth. Fallback to created_at for old payloads.
  const bracketNumberById = (() => {
    const hasOfficial = brackets.some((b) => b.official_number != null);
    if (hasOfficial) {
      const map = {};
      brackets.forEach((b) => { if (b.official_number != null) map[b._id] = b.official_number; });
      return map;
    }
    const sorted = [...brackets].sort(
      (a, b) => (a.created_at || '').localeCompare(b.created_at || '')
    );
    const map = {};
    sorted.forEach((b, i) => { map[b._id] = i + 1; });
    return map;
  })();

  useEffect(() => {
    const fetchChampionships = async () => {
      try {
        const { data } = await championshipApi.list({ status: 'in_progress' });
        const openChamps = await championshipApi.list({ status: 'open' });
        setChampionships([...data, ...openChamps.data]);
      } catch (error) { toast.error('Erro ao carregar campeonatos'); }
      finally { setLoading(false); }
    };
    fetchChampionships();
  }, []);

  useEffect(() => {
    if (selectedChampionship) {
      fetchBrackets();
      fetchRankings();

      // REACT_APP_BACKEND_URL can be unset when the backend serves the built
      // frontend itself (e.g. localhost:8000). Calling .replace() on undefined
      // used to throw and crash the whole page (black screen) right after
      // selecting a championship — fall back to same-origin like lib/api.js.
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
      try {
        wsRef.current = new WebSocket(`${wsUrl}/ws/championship_${selectedChampionship}`);
        wsRef.current.onmessage = () => {
          fetchBrackets();
          fetchRankings();
        };
      } catch (err) {
        // WebSocket is optional — manual refresh still reloads the data.
        wsRef.current = null;
      }
      return () => { if (wsRef.current) wsRef.current.close(); };
    }
  }, [selectedChampionship, rankingModality, combineRanking]);

  const fetchBrackets = async () => {
    try {
      const { data } = await championshipApi.getBrackets(selectedChampionship);
      setBrackets(data);
    } catch (error) { toast.error('Erro ao carregar chaves'); }
  };

  const fetchRankings = async () => {
    try {
      const mod = ['gi', 'nogi'].includes(rankingModality) ? rankingModality : undefined;
      const [lr, tr] = await Promise.all([
        championshipApi.getLiveRanking(selectedChampionship, mod),
        championshipApi.getTeamRanking(selectedChampionship, mod)
      ]);
      setLiveRanking(lr.data);
      setTeamRanking(tr.data);
    } catch (e) { /* rankings may not exist yet */ }
  };

  const handleRecordResult = async () => {
    if (!resultData.winner_id) { toast.error('Selecione o vencedor'); return; }
    setSaving(true);
    try {
      await matchApi.recordResult(selectedMatch._id, resultData);
      toast.success('Resultado registrado!');
      setSelectedMatch(null);
      setResultData({ winner_id: '', result_type: 'points', score1: 0, score2: 0 });
      fetchBrackets();
      fetchRankings();
      setMedalRefreshKey(k => k + 1);
    } catch (error) { toast.error('Erro ao registrar resultado'); }
    finally { setSaving(false); }
  };

  /**
   * Single-athlete bracket: the lone athlete has no opponent. The staff picks
   * one of four outcomes — champion / disqualified / over-weight / no-show.
   * Only "champion" assigns the gold medal; the others leave the category
   * without a winner.
   */
  const SINGLE_OUTCOMES = [
    { value: 'champion',     label: 'Campeão (sem adversário)',  result_type: 'wo',                 awardsMedal: true,  hint: 'Atleta presente e dentro do peso — recebe ouro' },
    { value: 'disqualified', label: 'Desclassificado',           result_type: 'disqualification',   awardsMedal: false, hint: 'Motivo 01 — desclassificação técnica/disciplinar' },
    { value: 'over_weight',  label: 'Estourou a balança',        result_type: 'over_weight',        awardsMedal: false, hint: 'Motivo 02 — acima do peso da categoria inscrita' },
    { value: 'no_show',      label: 'Não compareceu ao evento',  result_type: 'no_show',            awardsMedal: false, hint: 'Atleta ausente no dia da competição' },
  ];

  const handleSingleResult = async () => {
    if (!singleMatch?.athlete1_id) return;
    const outcome = SINGLE_OUTCOMES.find((o) => o.value === singleOutcome);
    if (!outcome) return;
    setSaving(true);
    try {
      await matchApi.recordResult(singleMatch._id, {
        winner_id: outcome.awardsMedal ? singleMatch.athlete1_id : null,
        result_type: outcome.result_type,
        score1: 0,
        score2: 0,
        disqualification_reason: outcome.awardsMedal ? null : outcome.value,
      });
      toast.success(
        outcome.awardsMedal
          ? `${singleMatch.athlete1_name} declarado(a) campeão(ã) por W.O.`
          : `Resultado registrado: ${outcome.label}`,
      );
      setSingleMatch(null);
      fetchBrackets();
      fetchRankings();
      setMedalRefreshKey(k => k + 1);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao registrar resultado');
    } finally {
      setSaving(false);
    }
  };

  const currentBracket = brackets.find(b => b._id === selectedBracket);

  const getMatchesSorted = (matches) => {
    if (!matches) return [];
    // Group: round matches first, then bronze match (round 0, position 99)
    const regular = matches.filter(m => m.match_type !== 'bronze').sort((a, b) => a.round - b.round || a.position - b.position);
    const bronze = matches.filter(m => m.match_type === 'bronze');
    return [...regular, ...bronze];
  };

  const getMatchLabel = (match, bracket) => {
    if (match.match_type === 'bronze') return 'DISPUTA DE BRONZE';
    if (match.match_type === 'single_athlete') return 'SEM ADVERSÁRIO';
    if (match.result_type === 'bye') return 'BYE';
    const totalRounds = bracket?.rounds || 1;
    if (match.round === totalRounds) return 'FINAL';
    if (match.round === totalRounds - 1) return 'SEMIFINAL';
    return `Rodada ${match.round}`;
  };

  return (
    <PageContent>
      <div className="space-y-6" data-testid="central-desk-page">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Chivo']">Mesa Central</h1>
          <p className="text-[#A1A1AA] mt-1">Gerencie as lutas e registre resultados em tempo real</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="space-y-2 flex-1 max-w-xs">
            <Label className="text-[#A1A1AA]">Campeonato</Label>
            <Select value={selectedChampionship} onValueChange={setSelectedChampionship}>
              <SelectTrigger data-testid="select-championship" className="bg-[#141414] border-[#27272A] text-white"><SelectValue placeholder="Selecione um campeonato" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                {championships.map((c) => <SelectItem key={c._id} value={c._id} className="text-white hover:bg-[#27272A]">{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {selectedChampionship && (
            <Button
              type="button"
              data-testid="open-telao-btn"
              onClick={() => window.open(`/public/${selectedChampionship}/telao`, '_blank', 'noopener')}
              className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold"
            >
              <Tv className="w-4 h-4 mr-2" />
              Abrir Telão
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
        ) : !selectedChampionship ? (
          <Card className="bg-[#141414] border-[#27272A]"><CardContent><EstadoVazio icone={Trophy} titulo="Selecione um campeonato" /></CardContent></Card>
        ) : (
          <Tabs defaultValue="lutas" className="w-full">
            <TabsList className="bg-[#141414] border border-[#27272A] mb-4">
              <TabsTrigger value="lutas" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Lutas</TabsTrigger>
              <TabsTrigger value="ranking" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Ranking Atletas</TabsTrigger>
              <TabsTrigger value="equipes" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Ranking Equipes</TabsTrigger>
              <TabsTrigger value="direct" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Lançamento Direto</TabsTrigger>
              {champMisto && (
                <div className="ml-2 flex items-center gap-2">
                  <Label className="text-[#A1A1AA] text-xs">Modalidade</Label>
                  <Select
                    value={rankingModality}
                    onValueChange={(v) => setRankingModality(v)}
                  >
                    <SelectTrigger data-testid="central-desk-ranking-modality" className="w-[150px] bg-[#1A1A1A] border-[#27272A] text-white h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      {rankingOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value} className="text-white hover:bg-[#27272A]">{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </TabsList>

            {/* ============ LUTAS TAB ============ */}
            <TabsContent value="lutas">
              {/* Bracket selector (searchable) + Code lookup */}
              <div className="mb-4 flex flex-col sm:flex-row gap-3">
                <div className="w-full sm:w-80">
                  <Popover open={bracketComboOpen} onOpenChange={setBracketComboOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      role="combobox"
                      aria-expanded={bracketComboOpen}
                      data-testid="select-bracket"
                      className="w-full flex items-center justify-between gap-2 rounded-md border border-[#27272A] bg-[#141414] px-3 py-2 text-sm text-left hover:border-[#3F3F46] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    >
                      <span className={`flex-1 truncate ${selectedBracket ? 'text-white' : 'text-[#71717A]'}`}>
                        {(() => {
                          const b = brackets.find((x) => x._id === selectedBracket);
                          if (!b) return 'Selecione uma categoria';
                          const n = bracketNumberById[b._id];
                          return `Nº ${n} · ${b.category_name} (${b.num_athletes})`;
                        })()}
                      </span>
                      <ChevronsUpDown className="w-4 h-4 text-[#71717A] shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="p-0 bg-[#141414] border-[#27272A] w-[var(--radix-popover-trigger-width)] max-h-[340px] overflow-hidden"
                    align="start"
                  >
                    <Command shouldFilter className="bg-[#141414]">
                      <div className="flex items-center border-b border-[#27272A] px-3">
                        <Search className="w-4 h-4 text-[#71717A] mr-2 shrink-0" />
                        <CommandInput
                          placeholder="Buscar categoria..."
                          className="flex h-10 w-full bg-transparent py-2 text-sm text-white outline-none placeholder:text-[#71717A] border-0"
                          data-testid="select-bracket-search"
                        />
                      </div>
                      <CommandList className="max-h-[280px] overflow-y-auto">
                        <CommandEmpty className="py-6 text-center text-sm text-[#71717A]">
                          Nenhuma categoria encontrada
                        </CommandEmpty>
                        <CommandGroup>
                          {brackets.map((b) => (
                            <CommandItem
                              key={b._id}
                              value={`${bracketNumberById[b._id] || ''} ${b.category_name} ${b.num_athletes} ${b.code || ''}`}
                              onSelect={() => {
                                setSelectedBracket(b._id);
                                setBracketComboOpen(false);
                              }}
                              className="text-white aria-selected:bg-[#D4AF37]/15 cursor-pointer"
                              data-testid={`select-bracket-option-${b._id}`}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${b._id === selectedBracket ? 'text-[#D4AF37]' : 'opacity-0'}`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="truncate flex items-center gap-2">
                                  <span className="inline-flex items-center justify-center min-w-[28px] px-1.5 h-5 rounded text-micro font-bold bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40">
                                    Nº {bracketNumberById[b._id] || '?'}
                                  </span>
                                  <span className="truncate">{b.category_name}</span>
                                </div>
                                <div className="text-xs text-[#71717A] mt-0.5">
                                  {b.num_athletes} atletas{b.code ? ` · Código ${String(b.code).padStart(6, '0')}` : ''}
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                </div>
                {/* Lookup by per-championship bracket number (visible on printed PDF) */}
                <div className="w-full sm:w-44">
                  <div className="relative">
                    <Input
                      data-testid="bracket-number-lookup"
                      type="text"
                      inputMode="numeric"
                      placeholder="Nº da chave"
                      title="Numero sequencial visivel na chave impressa (1, 2, 3...)"
                      value={bracketNumberQuery}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9]/g, '');
                        setBracketNumberQuery(v);
                        if (v.length === 0) return;
                        const target = parseInt(v, 10);
                        const matchId = Object.keys(bracketNumberById).find(
                          (id) => bracketNumberById[id] === target
                        );
                        if (matchId) setSelectedBracket(matchId);
                      }}
                      className="bg-[#141414] border-[#27272A] text-white placeholder:text-[#71717A] pr-9"
                    />
                    <Search className="w-4 h-4 text-[#71717A] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                  {bracketNumberQuery && !Object.values(bracketNumberById).includes(parseInt(bracketNumberQuery, 10)) && (
                    <p className="text-xs text-red-400 mt-1">Nº não encontrado</p>
                  )}
                </div>
                {/* Lookup by global bracket code (6 digits, printed on PDF header) */}
                <div className="w-full sm:w-44">
                  <div className="relative">
                    <Input
                      data-testid="bracket-code-lookup"
                      type="text"
                      inputMode="numeric"
                      placeholder="Código"
                      title="Codigo global da chave (ex: 000067)"
                      value={bracketCodeQuery}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9]/g, '');
                        setBracketCodeQuery(v);
                        if (v.length === 0) return;
                        const match = brackets.find((b) => String(b.code || '') === v);
                        if (match) setSelectedBracket(match._id);
                      }}
                      className="bg-[#141414] border-[#27272A] text-white placeholder:text-[#71717A] pr-9"
                    />
                    <Search className="w-4 h-4 text-[#71717A] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                  {bracketCodeQuery && !brackets.find((b) => String(b.code || '') === bracketCodeQuery) && (
                    <p className="text-xs text-red-400 mt-1">Código não encontrado</p>
                  )}
                </div>
              </div>

              {!selectedBracket ? (
                <Card className="bg-[#141414] border-[#27272A]"><CardContent><EstadoVazio icone={Users} titulo="Selecione uma categoria" /></CardContent></Card>
              ) : (
                <Card className="bg-[#141414] border-[#27272A]">
                  <CardHeader>
                    <CardTitle className="text-white font-['Chivo'] flex items-center gap-2">
                      <Award className="w-5 h-5 text-[#D4AF37]" />{currentBracket?.category_name}
                      <span className="text-[#71717A] text-sm font-normal ml-2">({currentBracket?.num_athletes} atletas)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {getMatchesSorted(currentBracket?.matches).map((match) => {
                        if (match.result_type === 'bye') return null;
                        const label = getMatchLabel(match, currentBracket);
                        const isBronze = match.match_type === 'bronze';
                        return (
                          <div
                            key={match._id}
                            className={`p-4 rounded-lg border ${match.winner_id ? 'bg-[#1A1A1A] border-green-500/30' : isBronze ? 'bg-[#141414] border-amber-700/40' : 'bg-[#141414] border-[#27272A]'}`}
                            data-testid={`match-${match._id}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded ${isBronze ? 'bg-[#CD7F32]/20 medalha-bronze' : label === 'FINAL' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-[#27272A] text-[#A1A1AA]'}`}>
                                {label}
                              </span>
                              {match.winner_id && (
                                <span className="text-xs text-green-500 flex items-center gap-1">
                                  <CheckCircle size={12} />
                                  {match.result_type === 'points' ? `${match.score1}-${match.score2}` :
                                   match.result_type === 'submission' ? 'Finalização' :
                                   match.result_type === 'wo' ? 'WO' : match.result_type}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <div className={`flex-1 p-2 rounded text-sm font-medium ${match.winner_id === match.athlete1_id ? 'bg-green-500/20 border border-green-500/50 text-green-400' : 'bg-[#0A0A0A] text-white'}`}>
                                {match.athlete1_name || 'Aguardando...'}
                              </div>
                              <span className="text-[#71717A] text-xs font-bold">VS</span>
                              <div className={`flex-1 p-2 rounded text-sm font-medium ${match.winner_id === match.athlete2_id ? 'bg-green-500/20 border border-green-500/50 text-green-400' : 'bg-[#0A0A0A] text-white'}`}>
                                {match.athlete2_name || 'Aguardando...'}
                              </div>
                              {!match.winner_id && match.athlete1_id && match.athlete2_id && (
                                <Button
                                  onClick={() => {
                                    setSelectedMatch(match);
                                    setResultData({ winner_id: '', result_type: 'points', score1: 0, score2: 0 });
                                  }}
                                  className="bg-[#D4AF37] text-black hover:bg-[#E5C158] text-xs shrink-0"
                                  size="sm"
                                  data-testid={`record-btn-${match._id}`}
                                >
                                  <Play size={16} className="mr-1" />Registrar
                                </Button>
                              )}
                              {/* Single-athlete bracket: open a dedicated dialog so the staff
                                  can either declare the athlete champion OR record a negative
                                  outcome (DQ, over-weight, no-show). */}
                              {!match.winner_id && match.athlete1_id && !match.athlete2_id && match.match_type === 'single_athlete' && (
                                <Button
                                  onClick={() => {
                                    setSingleMatch(match);
                                    setSingleOutcome('champion');
                                  }}
                                  className="bg-[#D4AF37] text-black hover:bg-[#E5C158] text-xs shrink-0"
                                  size="sm"
                                  data-testid={`record-btn-${match._id}`}
                                >
                                  <Play size={16} className="mr-1" />Registrar
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* ============ ENTREGA DE MEDALHAS (inline) ============ */}
              {currentBracket && (
                <div className="mt-6">
                  <MedalDeliveryWidget
                    championshipId={selectedChampionship}
                    bracketId={selectedBracket}
                    compact={true}
                    refreshKey={medalRefreshKey}
                  />
                </div>
              )}
            </TabsContent>

            {/* ============ RANKING ATLETAS TAB ============ */}
            <TabsContent value="ranking">
              <Card className="bg-[#141414] border-[#27272A]">
                <CardHeader><CardTitle className="text-white font-['Chivo'] flex items-center gap-2"><Trophy className="w-5 h-5 text-[#D4AF37]" />Ranking de Atletas (Tempo Real)</CardTitle></CardHeader>
                <CardContent>
                  {liveRanking.length === 0 ? (
                    <p className="text-[#71717A] text-center py-8">Nenhum resultado registrado ainda</p>
                  ) : (
                    <>
                      {/* Filtros — recortes da lista que ja esta na tela */}
                      <div className="rounded-lg border border-[#27272A] bg-[#1A1A1A]/40 p-3 mb-4" data-testid="athlete-ranking-filters">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          <div className="flex flex-col gap-1">
                            <Label className="micro-rotulo flex items-center gap-1"><Users className="w-3 h-3" />Atleta</Label>
                            <SearchableCombobox
                              testId="ranking-filter-athlete"
                              value={filterAthlete}
                              onChange={setFilterAthlete}
                              options={athleteFilterOptions}
                              placeholder="Todos os atletas"
                              searchPlaceholder="Buscar atleta..."
                              emptyText="Nenhum atleta com resultado"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <Label className="micro-rotulo flex items-center gap-1"><Shield className="w-3 h-3" />Equipe</Label>
                            <SearchableCombobox
                              testId="ranking-filter-team"
                              value={filterTeam}
                              onChange={setFilterTeam}
                              options={teamFilterOptions}
                              placeholder="Todas as equipes"
                              searchPlaceholder="Buscar equipe..."
                              emptyText="Nenhuma equipe com resultado"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <Label className="micro-rotulo flex items-center gap-1"><Medal className="w-3 h-3" />Categoria</Label>
                            <SearchableCombobox
                              testId="ranking-filter-category"
                              value={filterCategory}
                              onChange={setFilterCategory}
                              options={categoryFilterOptions}
                              placeholder="Todas as categorias"
                              searchPlaceholder="Buscar categoria..."
                              emptyText="Nenhuma categoria com resultado"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <Label className="micro-rotulo flex items-center gap-1"><Award className="w-3 h-3" />Kids</Label>
                            {/* Desabilitado com o motivo: o agrupamento Kids e uma
                                configuracao do campeonato (kids_enabled). */}
                            <SearchableCombobox
                              testId="ranking-filter-kids"
                              value={filterKids}
                              onChange={setFilterKids}
                              options={kidsEnabled ? kidsFilterOptions : []}
                              disabled={!kidsEnabled}
                              placeholder={kidsEnabled
                                ? 'Todos'
                                : (champConfigKnown ? 'Kids não está ativado neste campeonato' : 'Kids: configuração indisponível')}
                            />
                            <p className={`text-micro ${kidsEnabled ? 'text-emerald-400' : 'text-[#71717A]'}`}>
                              {kidsEnabled
                                ? `Ativado neste campeonato — agrupamento até ${kidsMaxAge} anos`
                                : 'Ative o agrupamento Kids no formulário do campeonato'}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                          <p className="text-[#71717A] text-xs" data-testid="ranking-filter-summary">
                            Mostrando <span className="text-white font-semibold">{filteredLiveRanking.length}</span> de {liveRanking.length} resultado{liveRanking.length > 1 ? 's' : ''}
                            {hasActiveRankingFilters ? ' (filtrado)' : ''}
                          </p>
                          {hasActiveRankingFilters && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={clearRankingFilters}
                              data-testid="ranking-clear-filters"
                              className="text-[#A1A1AA] hover:text-white border border-[#27272A]"
                            >
                              ✕ Limpar filtros
                            </Button>
                          )}
                        </div>
                      </div>

                      {filteredLiveRanking.length === 0 ? (
                        <p className="text-[#71717A] text-center py-8" data-testid="ranking-filter-empty">
                          Nenhum resultado para os filtros selecionados
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="tabela w-full" data-testid="athlete-ranking-table">
                            <thead>
                              <tr className="border-b border-[#27272A]">
                                <th className="text-left py-2 px-3 text-[#D4AF37] text-xs">Pos.</th>
                                <th className="text-left py-2 px-3 text-[#D4AF37] text-xs">Atleta</th>
                                <th className="text-left py-2 px-3 text-[#D4AF37] text-xs">Equipe</th>
                                <th className="text-left py-2 px-3 text-[#D4AF37] text-xs">Categoria</th>
                                <th className="text-right py-2 px-3 text-[#D4AF37] text-xs">Pts</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredLiveRanking.map((r, i) => (
                                <tr key={`${r.bracket_id || ''}-${r.athlete_id || i}-${r.place}`} className="border-b border-[#27272A]/50 hover:bg-[#1A1A1A]">
                                  <td className={`py-2 px-3 font-bold ${PLACE_COLORS[r.place] || 'text-white'}`}>{r.place}o</td>
                                  <td className="py-2 px-3 text-white text-sm">{r.athlete_name}</td>
                                  <td className="py-2 px-3 text-[#A1A1AA] text-xs">{r.team_name || '-'}</td>
                                  <td className="py-2 px-3 text-[#71717A] text-xs">{r.is_kids ? '🧒 ' : ''}{r.category_name}</td>
                                  <td className="py-2 px-3 text-right text-[#D4AF37] font-bold text-sm">{r.points}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ============ LANCAMENTO DIRETO TAB ============ */}
            <TabsContent value="direct">
              <DirectPlacementSection
                championshipId={selectedChampionship}
                brackets={brackets}
                bracketNumberById={bracketNumberById}
              />
            </TabsContent>

            {/* ============ RANKING EQUIPES TAB ============ */}
            <TabsContent value="equipes">
              <Card className="bg-[#141414] border-[#27272A]">
                <CardHeader><CardTitle className="text-white font-['Chivo'] flex items-center gap-2"><Shield className="w-5 h-5 text-[#D4AF37]" />Ranking por Equipe (Tempo Real)</CardTitle></CardHeader>
                <CardContent>
                  {teamRanking.length === 0 ? (
                    <p className="text-[#71717A] text-center py-8">Nenhum resultado registrado ainda</p>
                  ) : (
                    <div className="space-y-3" data-testid="team-ranking">
                      {teamRanking.map((team, i) => (
                        <div key={team.team_id} className="p-4 rounded-lg bg-[#1A1A1A] border border-[#27272A]">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <span className="text-[#D4AF37] font-bold text-lg w-8">{i + 1}o</span>
                              <h3 className="text-white font-bold">{team.team_name}</h3>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-[#D4AF37]" title="Ouro">{team.gold} Ouro</span>
                              <span className="medalha-prata" title="Prata">{team.silver} Prata</span>
                              <span className="medalha-bronze" title="Bronze">{team.bronze} Bronze</span>
                              <span className="text-white font-bold">{team.total_points} pts</span>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {team.athletes.map((a, j) => (
                              <span key={j} className={`text-xs px-2 py-0.5 rounded bg-[#0A0A0A] ${PLACE_COLORS[a.place]}`}>
                                {a.name} - {a.place}o ({a.category})
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Result Dialog */}
        <Dialog open={!!selectedMatch} onOpenChange={(open) => !open && setSelectedMatch(null)}>
          <DialogContent className="bg-[#141414] border-[#27272A] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-['Chivo']">Registrar Resultado</DialogTitle>
            </DialogHeader>
            {selectedMatch && (
              <div className="space-y-4 mt-4">
                <div className="text-center">
                  <p className="text-white font-bold">{selectedMatch.athlete1_name}</p>
                  <p className="text-[#71717A] text-sm">VS</p>
                  <p className="text-white font-bold">{selectedMatch.athlete2_name}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Vencedor *</Label>
                  <Select value={resultData.winner_id} onValueChange={(v) => setResultData({ ...resultData, winner_id: v })}>
                    <SelectTrigger data-testid="select-winner" className="bg-[#1A1A1A] border-[#27272A] text-white"><SelectValue placeholder="Selecione o vencedor" /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      <SelectItem value={selectedMatch.athlete1_id} className="text-white hover:bg-[#27272A]">{selectedMatch.athlete1_name}</SelectItem>
                      <SelectItem value={selectedMatch.athlete2_id} className="text-white hover:bg-[#27272A]">{selectedMatch.athlete2_name}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Tipo de Resultado</Label>
                  <Select value={resultData.result_type} onValueChange={(v) => setResultData({ ...resultData, result_type: v })}>
                    <SelectTrigger data-testid="select-result-type" className="bg-[#1A1A1A] border-[#27272A] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      <SelectItem value="points" className="text-white">Pontos</SelectItem>
                      <SelectItem value="submission" className="text-white">Finalizacao</SelectItem>
                      <SelectItem value="wo" className="text-white">WO</SelectItem>
                      <SelectItem value="disqualification" className="text-white">Desclassificacao</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {resultData.result_type === 'points' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Pontos {selectedMatch.athlete1_name?.split(' ')[0]}</Label>
                      <Input type="number" min="0" value={resultData.score1} onChange={(e) => setResultData({ ...resultData, score1: parseInt(e.target.value) || 0 })} data-testid="score1" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Pontos {selectedMatch.athlete2_name?.split(' ')[0]}</Label>
                      <Input type="number" min="0" value={resultData.score2} onChange={(e) => setResultData({ ...resultData, score2: parseInt(e.target.value) || 0 })} data-testid="score2" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setSelectedMatch(null)} className="border-[#27272A] text-[#A1A1AA]">Cancelar</Button>
                  <Button onClick={handleRecordResult} disabled={saving || !resultData.winner_id} data-testid="save-result" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ============ SINGLE-ATHLETE RESULT DIALOG ============ */}
        <Dialog open={!!singleMatch} onOpenChange={(open) => !open && !saving && setSingleMatch(null)}>
          <DialogContent className="bg-[#141414] border-[#27272A] max-w-md flex flex-col" data-testid="single-athlete-result-dialog">
            <DialogHeader>
              <DialogTitle className="text-white font-['Chivo']">Registrar Resultado</DialogTitle>
            </DialogHeader>
            {singleMatch && (
              <div className="space-y-4 mt-2">
                <div className="rounded-lg bg-[#1A1A1A] border border-[#27272A] p-3 text-sm">
                  <div className="text-[#71717A] text-xs uppercase tracking-wide mb-1">Atleta sem adversário</div>
                  <div className="text-white font-medium">{singleMatch.athlete1_name}</div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Selecione o resultado</Label>
                  <div className="space-y-2" data-testid="single-outcome-options">
                    {SINGLE_OUTCOMES.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => setSingleOutcome(o.value)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          singleOutcome === o.value
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                            : 'border-[#27272A] bg-[#1A1A1A] hover:border-[#3F3F46]'
                        }`}
                        data-testid={`single-outcome-${o.value}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-1 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                            singleOutcome === o.value ? 'border-[#D4AF37]' : 'border-[#52525B]'
                          }`}>
                            {singleOutcome === o.value && (
                              <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className={`font-medium ${singleOutcome === o.value ? 'text-[#D4AF37]' : 'text-white'}`}>
                              {o.label}
                              {o.awardsMedal && (
                                <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/40">
                                  Ouro
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-[#71717A] mt-0.5">{o.hint}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="sticky bottom-0 z-10 -mx-6 -mb-6 flex justify-end gap-2 border-t border-[#27272A] bg-[#141414] px-6 py-3">
                  <Button
                    variant="outline"
                    onClick={() => setSingleMatch(null)}
                    disabled={saving}
                    className="border-[#27272A] text-[#A1A1AA]"
                    data-testid="single-result-cancel"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSingleResult}
                    disabled={saving || !singleOutcome}
                    className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
                    data-testid="single-result-save"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </PageContent>
  );
}
